<template>
  <base-page-layout
    class="melhores-clientes"
    title="Melhores clientes"
    :filters="['lojas', 'pessoa', 'data', 'produtos']"
    default-date="ultimosTrintaDias"
    quick-actions
    quick-actions-default-title="Melhores clientes"
  >
    <mf-card-container title="Ranking por faturamento">
      <template v-slot:title-complement>
        <mf-quick-actions
          ref="quickAction"
          quick-action-group-id="best-clients"
          short-description="Melhores clientes"
          :long-description="quickActionLongDescription"
          :disabled="$wait.is(['melhores-clientes'])"
          :segmentation-group="quickActionSegmentationGroup"
        />
      </template>
      <template v-slot:filters>
        <export-data is-pii :data-fetcher="getDataToExport" filename="ranking_por_faturamento" ghost />
        <headers-data-table-settings
          key="best-costumers-table"
          :headers.sync="headers"
          :default-headers="getDefaultHeadersOptions()"
          headers-settings-name="offer_results_coupons_per_unit"
        />
      </template>
      <mf-loading :loader="['last-update', 'melhores-clientes']" :boolean-loader="isLoading" loader-type="table">
        <v-data-table :items="tableItemsWithIndex" :headers="computedHeaders" :items-per-page-options="[10, 25, { text: 'Todos', value: -1 }]">
          <template v-slot:[`item.NOM_NOME`]="{ item }">
            <mf-tooltip top max-width="350px">
              <template v-slot:activator="{ on }">
                <a v-if="hasPiiAccess" class="link" target="_blank" :href="`#/clientes/detalhes-cliente/${item['COD_ID_CLIENTE']}`">{{
                  putEllipsis(item['NOM_NOME'], 20)
                }}</a>
                <span v-else>{{ putEllipsis(item['NOM_NOME'], 20) }}</span>
              </template>
              <span>{{ item['NOM_NOME'] }}</span>
            </mf-tooltip>
          </template>
          <template v-slot:[`item.VAL_FATURAMENTO_TOTAL`]="{ item }">
            {{ item['VAL_FATURAMENTO_TOTAL'] | localeString(2) | currency }}
          </template>
          <template v-slot:[`item.TICKET_MEDIO`]="{ item }">
            {{ item['TICKET_MEDIO'] | abreviation({ isCurrency: true }) | currency }}
          </template>
          <template v-slot:[`item.DES_TIPO_CLIENTE`]="{ item }">
            {{ item['DES_TIPO_CLIENTE'] | filtroPessoa }}
          </template>
          <template v-slot:[`item.ARR_COD_ID_LOJAS`]="{ item }">
            <v-tooltip v-if="item['ARR_COD_ID_LOJAS'].length > 1" right>
              <template v-slot:activator="{ on, attrs }">
                <span v-bind="attrs" style="cursor: auto" v-on="on">
                  {{ getUnits(item) }}
                </span>
              </template>
              <span v-for="(unit, index) in item['ARR_COD_ID_LOJAS']" :key="`${unit}-${index}`" style="display: block">
                {{ unitsNames[unit] }}
              </span>
            </v-tooltip>
            <span v-else-if="item['ARR_COD_ID_LOJAS'].length === 1">
              {{ unitsNames[item['ARR_COD_ID_LOJAS'][0]] }}
            </span>
            <span v-else> - </span>
          </template>
          <template v-slot:[`item.NUM_ULTIMA_VISITA`]="{ item }">
            {{ DateTime.fromFormat(item['NUM_ULTIMA_VISITA'].toString(), 'yyyyMMdd').toFormat('dd/MM/yyyy') }}
          </template>
        </v-data-table>
      </mf-loading>
    </mf-card-container>
  </base-page-layout>
</template>

<script>
import { mapState, mapGetters } from 'vuex'
import orderBy from 'lodash/orderBy'
import cloneDeep from 'lodash/cloneDeep'
import { DateTime } from 'luxon'
import { mapWaitingActions } from 'vue-wait'
import { createCsvFromArray, putEllipsis } from '@/config/helpers.js'
import ExportData from '@/components/export-data/ExportData.vue'
import BasePageLayout from '@/components/BasePageLayout.vue'
import { criteriaGroup, listOfIdsCriteria } from '@/components/quick-actions/segmentationGroups.js'
import HeadersDataTableSettings from '@/components/HeadersDataTableSettings.vue'

export default {
  components: {
    ExportData,
    BasePageLayout,
    HeadersDataTableSettings,
  },
  filters: {
    filtroPessoa(value) {
      if (value === 'F') return 'PF'
      if (value === 'J') return 'PJ'
      return '-'
    },
  },
  data() {
    return {
      DateTime: DateTime,
      headers: [],
      loading: true,
    }
  },
  computed: {
    ...mapState({
      stateMelhoresClientes: (state) => state.clientes.melhoresClientes,
      stateFilters: (state) => state.filters,
    }),
    ...mapGetters(['units']),
    unitsNames() {
      if (!this.units?.length) return {}
      return this.units.reduce((acc, unit) => ({ ...acc, [unit.id_erp]: unit.name }), {})
    },
    isLoading() {
      return this.loading && !this.tableItemsWithIndex?.length
    },
    quickActionSegmentationGroup() {
      return criteriaGroup([listOfIdsCriteria(this.clientIds, 'Melhores clientes')])
    },
    quickActionLongDescription() {
      return (formatters) => `Melhores clientes - ${formatters.startDate} a ${formatters.endDate}${formatters.filtered}`
    },
    clientIds() {
      if (!this.tableItemsWithIndex) return []
      return this.tableItemsWithIndex.map(({ COD_ID_CLIENTE }) => COD_ID_CLIENTE)
    },
    melhoresClientes() {
      const bestClients = Array.isArray(this.stateMelhoresClientes) ? this.stateMelhoresClientes : [this.stateMelhoresClientes]
      let array = bestClients.map((state) => {
        if (state['NUM_IDADE'] === 0) {
          state['NUM_IDADE'] = '-'
        }
        state['TICKET_MEDIO'] = state['VAL_FATURAMENTO_TOTAL'] / state['NUM_CUPONS']
        return state
      })
      return orderBy(array, 'VAL_FATURAMENTO_TOTAL', 'desc')
    },
    tableItemsWithIndex() {
      return this.melhoresClientes.map((element, index) => {
        element.index = index + 1
        return element
      })
    },
    computedHeaders() {
      return this.headers.filter((header) => header.isActive)
    },
    defaultHeaders() {
      return [
        {
          text: 'Posição',
          value: 'index',
          width: '4%',
          isActive: true,
          isDefault: true,
          isRequired: false,
        },
        {
          text: 'Nome',
          value: 'NOM_NOME',
          align: 'left',
          width: '10%',
          isActive: true,
          isDefault: true,
          isRequired: false,
        },
        {
          text: 'Faturamento total',
          align: 'left',
          value: 'VAL_FATURAMENTO_TOTAL',
          width: '10%',
          isActive: true,
          isDefault: true,
          isRequired: false,
        },
        {
          text: 'Total cupons',
          align: 'left',
          value: 'NUM_CUPONS',
          width: '8%',
          isActive: true,
          isDefault: true,
          isRequired: false,
        },
        {
          text: 'Ticket médio',
          align: 'left',
          value: 'TICKET_MEDIO',
          width: '6%',
          isActive: true,
          isDefault: true,
          isRequired: false,
        },
        {
          text: 'Bairro',
          sortable: false,
          align: 'left',
          value: 'DES_BAIRRO',
          width: '10%',
          isActive: false,
          isDefault: false,
          isRequired: false,
        },
        {
          text: 'Tipo de pessoa',
          align: 'left',
          value: 'DES_TIPO_CLIENTE',
          width: '4%',
          sortable: false,
          isActive: true,
          isDefault: true,
          isRequired: false,
        },
        {
          text: 'Idade',
          align: 'left',
          value: 'NUM_IDADE',
          width: '5%',
          sortable: false,
          isActive: false,
          isDefault: false,
          isRequired: false,
        },
        {
          text: 'Sexo',
          sortable: false,
          align: 'left',
          value: 'DES_SEXO_CLIENTE',
          width: '5%',
          isActive: false,
          isDefault: false,
          isRequired: false,
        },
        {
          text: 'Lojas visitadas',
          sortable: false,
          align: 'left',
          value: 'ARR_COD_ID_LOJAS',
          width: '10%',
          isActive: true,
          isDefault: false,
          isRequired: false,
        },
        {
          text: 'Última visita',
          value: 'NUM_ULTIMA_VISITA',
          align: 'left',
          width: '9%',
          isActive: true,
          isDefault: false,
          isRequired: false,
        },
      ]
    },
  },
  async created() {
    this.setFilters({ ...this.stateFilters })
    await this.$nextTick(() => {
      this.$bus.$on('filter', this.getData)
    })
  },
  destroyed() {
    this.$bus.$off('filter')
  },
  methods: {
    putEllipsis,
    ...mapWaitingActions({
      melhoresClientesTop: 'melhores-clientes',
      setFilters: 'set-filters',
    }),
    getDefaultHeadersOptions() {
      return cloneDeep(this.defaultHeaders)
    },
    getDataToExport() {
      const headers = [
        'Código Cliente',
        'CPF/CNPJ',
        'Nome Cliente',
        'Faturamento',
        'Cupons',
        'Última visita',
        'País',
        'Estado',
        'Cidade',
        'Bairro',
        'Rua',
        'Número',
        'Fone 1',
        'Fone 2',
        'Gênero',
        'Idade',
        'Tipo cliente',
      ]
      const rows = this.tableItemsWithIndex.map((item) => {
        return [
          item['COD_ID_CLIENTE'],
          item['NUM_CPF_CNPJ'],
          item['NOM_NOME'],
          item['VAL_FATURAMENTO_TOTAL'],
          item['NUM_CUPONS'],
          DateTime.fromFormat(item['NUM_ULTIMA_VISITA'].toString(), 'yyyyMMdd').toFormat('dd/MM/yyyy'),
          item['DES_PAIS'],
          item['DES_ESTADO'],
          item['DES_CIDADE'],
          item['DES_BAIRRO'],
          item['DES_ENDERECO'],
          item['NUM_NUMERO'],
          item['DES_FONE_1'],
          item['DES_FONE_2'],
          item['DES_SEXO_CLIENTE'],
          item['NUM_IDADE'],
          item['DES_TIPO_CLIENTE'],
        ]
      })
      return { headers, rows }
    },
    exportCsv() {
      const arrayToExport = this.tableItemsWithIndex.map((obj) => {
        return {
          id: obj['COD_ID_CLIENTE'],
          cpf_cnpj: obj['NUM_CPF_CNPJ'],
          nome: obj['NOM_NOME'],
          faturamento: obj['VAL_FATURAMENTO_TOTAL'],
          cupons: obj['NUM_CUPONS'],
          ultima_visita: obj['NUM_ULTIMA_VISITA'],
          rua: obj['DES_ENDERECO'],
          numero: obj['NUM_NUMERO'],
          bairro: obj['DES_BAIRRO'],
          cidade: obj['DES_CIDADE'],
          estado: obj['DES_ESTADO'],
          pais: obj['DES_PAIS'],
          fone_1: obj['DES_FONE_1'],
          fone_2: obj['DES_FONE_2'],
          genero: obj['DES_SEXO_CLIENTE'],
          idade: obj['NUM_IDADE'],
          tipo_cliente: obj['DES_TIPO_CLIENTE'],
        }
      })
      const headers = [
        'Cod Cliente',
        'CPF_CPNJ',
        'Nome Cliente',
        'Faturamento',
        'Cupons',
        'Última visita',
        'Rua',
        'Número',
        'Bairro',
        'Cidade',
        'Estado',
        'País',
        'Fone 1',
        'Fone 2',
        'Genero',
        'Idade',
        'Tipo cliente',
      ]
      createCsvFromArray(arrayToExport, 'melhores_clientes', headers)
    },

    getUnits(item) {
      if (!item['ARR_COD_ID_LOJAS'] || !item['ARR_COD_ID_LOJAS'].length) return '-'
      else if (item['ARR_COD_ID_LOJAS'].length >= 2) return `${item['ARR_COD_ID_LOJAS'].length} lojas`
      return this.unitsNames[item['ARR_COD_ID_LOJAS'][0]]
    },
    async getData() {
      try {
        const filter = {
          date: Array.isArray(this.stateFilters.data) ? this.stateFilters.data.filter(Boolean) : [],
          unidades: this.stateFilters.unitsConfiguration,
          tipo_pessoa: this.stateFilters.pessoa,
          categoria_cliente: this.stateFilters.cliente,
          fidelidade: 1,
          produto: this.stateFilters.products,
        }

        await this.melhoresClientesTop(filter)
        this.$refs.quickAction.refetch()
      } catch (error) {
        console.error(error)
      } finally {
        this.loading = false
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.melhores-clientes {
  .melhores-clientes-card {
    min-height: 250px;
  }
}
::v-deep tbody {
  td {
    color: $cinza-0;
    font-size: 14px;
    line-height: 1;
  }
}
</style>
